define((require) => {
    "use strict";

    const _        = require("underscore");
    const $        = require("jquery");
    const Backbone = require("backbone");
    const Time     = require("shared/services/Time");
    const Storage  = require("shared/services/Storage");

    /**
     * Trackable Editor locations.
     */
    const Location = {
        DEFAULT:                       "",
        LOGIN:                         "login",
        DASHBOARD_EXPERIENCES:         "dashboard",
        DASHBOARD_IMAGES:              "imageLibrary",
        DASHBOARD_PUBLISH:             "publish",
        DASHBOARD_TEMPLATES:           "templates",
        PROJECT_DASHBOARD_EXPERIENCES: "projectDashboardExperiences",
        PROJECT_DASHBOARD_IMAGES:      "projectDashboardImages",
        PROJECT_DASHBOARD_PUBLISH:     "projectDashboardPublish",
        PROJECT_CREATE:                "projectCreate",
        REPORTING:                     "reporting",
        REPORTING_SEGMENT:             "reportingSegment",
        EDIT:                          "edit",
        MEASURE:                       "measure",
        MANAGE_ACCOUNT:                "manageAccount",
        PSD_IMPORT:                    "psdImport",
        FONTS:                         "fonts",
        ANALYTICS:                     "analytics",
        EXPORT:                        "export",
        BREAKPOINTS:                   "breakpoints",
        INTEGRATIONS:                  "integrations",
        PREVIEW:                       "preview",
        PREVIEW_TEMPLATE:              "previewTemplate"
    };

    // Returns a storage key for the specified user to track last visited
    const lastVisitKey = function(user) {
        return `lastVisit-${user.get("id")}`;
    };

    // Returns stored last visit key...
    const lastVisit = function(user) {
        const key = lastVisitKey(user);

        return Storage.Local.get(Storage.Keys.LOGIN, key) ||
               JSON.parse(localStorage.getItem(key)) || // legacy issue, did not use storage
               {};
    };

    /**
     * Model containing cached data for the user associated with the currently logged in principal. Once all data
     * is ready for said user, the a change event for the <tt>ready</tt> property will fire. On principal logout,
     * the change event will fire again. <p>
     *
     * Do <b>NOT</b> use the <tt>user</tt> property to test for permissions, use <tt>LoggedIn.principal()</tt> instead.
     * The <tt>user</tt> property set here is the user available via <tt>principal.get("user")</tt> once the
     * principal on question has been logged in successfully. <p>
     *
     * This model is initialized as a singleton. The rationale for this is that this information is used
     * virtually everywhere in the application, so access should be as easy as possible. The contents of the model can
     * be mocked to ensure testability. <p>
     *
     * Avoid adding dependencies to our internal classes, as we want this class to be available everywhere without
     * needing to worry about circular dependencies. As a result of this rule, the logic for fetching collections
     * associated with the user is done in AuthenticationController.<p>
     *
     * @class LoggedInUserState
     *
     * @cfg {Boolean}                       ready       Whether a principal for <tt>user</tt> has logged in *and* all global
     *                                                  data for <tt>user</tt> is guaranteed to be available.
     * @cfg {User}                          user        The user model. Do <b>NOT</b> use this user to test for permissions,
     *                                                  use <tt>LoggedIn.principal()</tt> instead!
     * @cfg {Company}                       company     The company model for the company of <tt>user</tt>.
     * @cfg {CompanySettings}               settings    The company settings model for <tt>company</tt>.
     * @cfg {Boolean}                       internal    Whether the user is logged in from an internal IP address.
     * @cfg {ExperienceMetadataCollection}  experiences The experience collection for the current user.
     * @cfg {ProjectCollection}             projects    The project collection for the current user.
     * @cfg {ImageCollection}               images      The image collection for the current user.
     */
    const LoggedInUserState = Backbone.Model.extend({

        _collectionPromiseCache: [],

        defaults: {
            ready:           false,
            user:            null,
            company:         null,
            companySettings: null,
            experiences:     null,
            projects:        null,
            images:          null,

            /**
             * @type IntegrationCollection
             */
            integrations:    null,

            location:        Location.DEFAULT,
            lastVisit:       {}
        },

        /**
         * Sets the user model to the given user, updating related properties. <p>
         *
         * This does not trigger a change event for <tt>ready</tt>, as other things like loading global collections
         * needs to be done first (externally to this class).
         *
         * @param {User} user The user associated with the currently logged in principal; cannot be null.
         */
        setUser(user) {
            this.set({
                user,
                lastVisit: lastVisit(user)
            });
        },

        /**
         * Clear all states, reverting to the default values. This is called when the AuthenticationController
         * logs a principal out. <p>
         *
         * This will trigger (among others) a change event for <tt>ready</tt>.
         */
        clear() {
            this.set(this.defaults);
            this._collectionPromiseCache = [];
        },

        /**
         * Register a visit to an experience.
         *
         * @param {String} experienceId The ID of the experience visited.
         * @param {number} [offset] Offset to add to the timestamp. Defaults to 0.
         */
        registerExperienceVisit(experienceId, offset) {
            if (experienceId) {
                const lastVisit = this.get("lastVisit");

                // Initialize experience object if not already registered
                if (!lastVisit[experienceId] || !lastVisit[experienceId].sceneVisits) {
                    lastVisit[experienceId] = {sceneVisits: {}};
                }

                lastVisit[experienceId].visit = Time.server() + (offset || 0);
                this.set("lastVisit", lastVisit);
                Storage.Local.update(Storage.Keys.LOGIN, lastVisitKey(this.get("user")), lastVisit);
            }
        },

        /**
         * Register a visit to a scene.
         * @param {String} experienceId The ID of the experience containing the scene.
         * @param {String} sceneId The ID of the scene.
         */
        registerSceneVisit(experienceId, sceneId) {
            if (experienceId && sceneId) {
                const lastVisit = this.get("lastVisit");

                if (lastVisit[experienceId]) {
                    lastVisit[experienceId].sceneVisits[sceneId] = lastVisit[experienceId].visit;
                    this.set("lastVisit", lastVisit);
                    Storage.Local.update(Storage.Keys.LOGIN, lastVisitKey(this.get("user")), lastVisit);
                }
            }
        },

        /**
         * Gets an object describing the last visit to an experience and its scenes.
         *
         * <p>The object contains the following properties:</p>
         * <ul>
         *     <li><b>visit</b>: The timestamp of the last visit to the experience in server time. Will be 0 if the experience has not been visited.</li>
         *     <li><b>sceneVisits</b>: A hash of scene ID to last visit to the scene. Will be empty if the experience has not been visited.</li>
         * </ul>
         *
         * @param {String} experienceId The ID of the experience.
         * @returns {Object} Object as described above.
         */
        getLastExperienceVisit(experienceId) {
            const experienceLastVisit = this.get("lastVisit")[experienceId];

            if (experienceLastVisit) {
                // Clone to decouple the objects
                return JSON.parse(JSON.stringify(experienceLastVisit));
            }

            return {visit: 0, sceneVisits: {}};
        },

        /**
         * Fetches a collection that should be cached globally. For example, pass a UserCollection to get the collection
         * of users on the same company as the logged-in user.
         *
         * @param {Function} Type A type of collection to fetch.
         * @returns {Promise} A promise that will resolve to the fetched collection.
         */
        fetchGlobalCollection(Type) {
            let collectionPromise;

            // Attempt to find it in the cache
            _.every(this._collectionPromiseCache, (cachedCollectionPromise) => {
                if (cachedCollectionPromise.Type === Type) {
                    collectionPromise = cachedCollectionPromise;

                    // Don't bother checking the next promise
                    return false;
                }

                // Check the next promise
                return true;
            });

            // If not found, create it and push it into the cache
            if (!collectionPromise) {
                collectionPromise = $.Deferred((deferred) => {
                    const collection = new Type();
                    collection.fetch().done(() => {
                        deferred.resolve(collection);
                    });
                }).promise();
                collectionPromise.Type = Type;
                this._collectionPromiseCache.push(collectionPromise);
            }

            return collectionPromise;
        },

        /**
         * Invalidates the cached global collection of the given type, so it will be fetched from the server the next
         * time someone requests it.
         *
         * @param {Function} Type A type of collection to invalidate.
         */
        invalidateGlobalCollection(Type) {
            this._collectionPromiseCache = _.filter(this._collectionPromiseCache, (cachedCollectionPromise) => (
                cachedCollectionPromise.Type !== Type
            ));
        },

        Location

    });

    // Singleton
    return new LoggedInUserState();
});



// WEBPACK FOOTER //
// ./src/shared/ui/user/LoggedInUserState.js